import { useCallback, useState } from 'react'
import { atom, useAtomValue, useSetAtom } from 'jotai'
import { atomFamily } from 'jotai/utils'
import { logger } from '@/services/logger/logger.client'
import { type Form } from '@/services/PriceCalculator/PriceCalculator.types'
import { priceTemplateAtom } from './PurchaseForm/priceTemplateAtom'
import { usePriceCalculatorForm } from './usePriceCalculatorForm'
import { usePriceIntent } from './usePriceIntent'

const activeFormSectionIdAtomFamily = atomFamily((id: string) => {
  const sectionAtom = atom<string | null>(null)
  sectionAtom.debugLabel = `activeFormSection/${id}`
  return sectionAtom
})

export const GOTO_NEXT_SECTION = Symbol('GOTO_NEXT_SECTION')

const getFirstIncompleteSectionId = (form: Form): string => {
  const firstIncompleteSection = form.sections.find(({ state }) => state !== 'valid')
  return firstIncompleteSection?.id ?? form.sections[form.sections.length - 1].id
}

export const useActiveFormSectionId = () => {
  const { priceIntentId } = usePriceIntent()
  const form = usePriceCalculatorForm()
  const priceTemplate = useAtomValue(priceTemplateAtom)

  // Get the initial active section only once to prevent automatic section changes on price intent updates
  const [initialActiveSectionId] = useState(() => getFirstIncompleteSectionId(form))

  if (!priceIntentId) {
    throw new Error('priceIntentId not found')
  }

  const activeSectionId = useAtomValue(activeFormSectionIdAtomFamily(priceIntentId))
  const setActiveSectionId = useSetAtom(activeFormSectionIdAtomFamily(priceIntentId))

  const activeFormSectionId = activeSectionId ?? initialActiveSectionId

  const setActiveFormSectionId = useCallback(
    (newValue: string | typeof GOTO_NEXT_SECTION) => {
      if (newValue !== GOTO_NEXT_SECTION) {
        setActiveSectionId(newValue)
        return
      }

      // Handle navigating to next section
      const currentSectionIndex = form.sections.findIndex(({ id }) => id === activeFormSectionId)

      const nextSection = form.sections[currentSectionIndex + 1]
      const isNextSectionExists = currentSectionIndex < form.sections.length - 1

      if (isNextSectionExists) {
        setActiveSectionId(nextSection.id)
      } else {
        logger.error('Failed to find next section', {
          currentSectionId: activeFormSectionId,
          templateName: priceTemplate?.name ?? 'unknown',
          priceIntentId,
        })
      }
    },
    [form, priceIntentId, priceTemplate, activeFormSectionId, setActiveSectionId],
  )

  return { activeFormSectionId, setActiveFormSectionId }
}
